import { useState, useEffect, useCallback, useRef } from 'react';
import { useBusiness } from '@/contexts/BusinessContext';
import { decryptWiseKey, decryptMercuryKey } from '@/lib/encryption';
import { syncWiseFeesAsExpenses } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import LogoSpinner from '@/components/LogoSpinner';
import {
  fetchWiseProfiles,
  fetchWiseBalances,
  fetchWiseStatement,
  type WiseProfile,
  type WiseBalance,
  type WiseStatementTransaction,
} from '@/lib/wiseService';
import {
  fetchMercuryAccounts,
  fetchMercuryTransactions,
  type MercuryAccount,
  type MercuryTransaction,
} from '@/lib/mercuryService';
import {
  Wallet,
  RefreshCw,
  Plus,
  TrendingUp,
  TrendingDown,
  ArrowLeftRight,
  CircleAlert as AlertCircle,
  Building2,
} from 'lucide-react';
import BalanceCard from '@/pages/balances/BalanceCard';
import MercuryAccountCard from '@/pages/balances/MercuryAccountCard';
import StatementSection from '@/pages/balances/StatementSection';
import MercuryTransactionsSection from '@/pages/balances/MercuryTransactionsSection';
import WiseSetupModal from '@/pages/balances/WiseSetupModal';
import MercurySetupModal from '@/pages/balances/MercurySetupModal';

type Provider = 'wise' | 'mercury';

function formatCurrencyValue(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export default function BalancesPage() {
  const { selectedBusiness, refreshBusinesses } = useBusiness();

  const [activeProvider, setActiveProvider] = useState<Provider>('wise');

  const [wiseConnected, setWiseConnected] = useState(false);
  const [wiseKey, setWiseKey] = useState<string | null>(null);
  const [, setProfiles] = useState<WiseProfile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<WiseProfile | null>(null);
  const [balances, setBalances] = useState<WiseBalance[]>([]);
  const [selectedBalance, setSelectedBalance] = useState<WiseBalance | null>(null);
  const [transactions, setTransactions] = useState<WiseStatementTransaction[]>([]);

  const [mercuryConnected, setMercuryConnected] = useState(false);
  const [mercuryKey, setMercuryKey] = useState<string | null>(null);
  const [mercuryAccounts, setMercuryAccounts] = useState<MercuryAccount[]>([]);
  const [selectedMercuryAccount, setSelectedMercuryAccount] = useState<MercuryAccount | null>(null);
  const [mercuryTransactions, setMercuryTransactions] = useState<MercuryTransaction[]>([]);
  const [mercuryTxnLoading, setMercuryTxnLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statementLoading, setStatementLoading] = useState(false);
  const [error, setError] = useState('');
  const [showWiseSetup, setShowWiseSetup] = useState(false);
  const [showMercurySetup, setShowMercurySetup] = useState(false);

  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    initialize();

    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(() => {
      if (!selectedBusiness) return;
      silentRefresh();
    }, 60_000);

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [selectedBusiness]);

  useEffect(() => {
    if (!selectedBusiness) return;

    const channel = supabase
      .channel(`balances_business_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'businesses', filter: `id=eq.${selectedBusiness.id}` },
        () => { silentRefresh(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [selectedBusiness]);

  const initialize = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    setError('');

    try {
      const [wKey, mKey] = await Promise.all([
        decryptWiseKey(selectedBusiness.wise_api_key_encrypted),
        decryptMercuryKey(selectedBusiness.mercury_api_key_encrypted),
      ]);

      if (wKey) {
        setWiseKey(wKey);
        setWiseConnected(true);
      } else {
        setWiseConnected(false);
        setWiseKey(null);
      }

      if (mKey) {
        setMercuryKey(mKey);
        setMercuryConnected(true);
      } else {
        setMercuryConnected(false);
        setMercuryKey(null);
      }

      await Promise.all([
        wKey ? loadWiseData(wKey) : Promise.resolve(),
        mKey ? loadMercuryData(mKey) : Promise.resolve(),
      ]);

      if (!wKey && mKey) {
        setActiveProvider('mercury');
      } else if (wKey) {
        setActiveProvider('wise');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to initialize');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  async function loadWiseData(key: string) {
    try {
      const wiseProfiles = await fetchWiseProfiles(key);
      setProfiles(wiseProfiles);

      const businessProfile = wiseProfiles.find((p) => p.type === 'BUSINESS') || wiseProfiles[0];
      if (businessProfile) {
        setSelectedProfile(businessProfile);
        const wiseBalances = await fetchWiseBalances(key, businessProfile.id);
        const visible = wiseBalances.filter((b) => b.visible !== false);
        setBalances(visible);

        if (visible.length > 0) {
          setSelectedBalance(visible[0]);
          await loadStatement(key, businessProfile.id, visible[0]);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load Wise data');
    }
  }

  async function loadMercuryData(key: string) {
    try {
      const response = await fetchMercuryAccounts(key);
      const accounts = response.accounts || [];
      setMercuryAccounts(accounts);

      if (accounts.length > 0) {
        setSelectedMercuryAccount(accounts[0]);
        await loadMercuryTxns(key, accounts[0].id);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load Mercury data');
    }
  }

  async function loadMercuryTxns(key: string, accountId: string) {
    setMercuryTxnLoading(true);
    try {
      const response = await fetchMercuryTransactions(key, accountId, undefined, undefined, 500);
      const txns = response.transactions || [];
      setMercuryTransactions(txns);
    } catch {
      setMercuryTransactions([]);
    } finally {
      setMercuryTxnLoading(false);
    }
  }

  async function loadStatement(key: string, profileId: number, balance: WiseBalance) {
    setStatementLoading(true);
    try {
      const start = balance.creationTime || '2020-01-01T00:00:00.000Z';
      const end = new Date().toISOString();
      const statement = await fetchWiseStatement(key, profileId, balance.id, balance.currency, start, end);
      const txns = statement.transactions || [];
      setTransactions(txns);

      if (selectedBusiness && txns.length > 0) {
        syncWiseFeesAsExpenses(selectedBusiness.id, txns, selectedBusiness.bank_rate).catch(() => {});
      }
    } catch {
      setTransactions([]);
    } finally {
      setStatementLoading(false);
    }
  }

  async function silentRefresh() {
    try {
      await Promise.all([
        wiseKey ? loadWiseData(wiseKey) : Promise.resolve(),
        mercuryKey ? loadMercuryData(mercuryKey) : Promise.resolve(),
      ]);
    } catch {}
  }

  async function handleRefresh() {
    setRefreshing(true);
    setError('');
    try {
      if (activeProvider === 'wise' && wiseKey) {
        await loadWiseData(wiseKey);
      } else if (activeProvider === 'mercury' && mercuryKey) {
        await loadMercuryData(mercuryKey);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  }

  async function handleBalanceSelect(balance: WiseBalance) {
    setSelectedBalance(balance);
    if (wiseKey && selectedProfile) {
      await loadStatement(wiseKey, selectedProfile.id, balance);
    }
  }

  async function handleMercuryAccountSelect(account: MercuryAccount) {
    setSelectedMercuryAccount(account);
    if (mercuryKey) {
      await loadMercuryTxns(mercuryKey, account.id);
    }
  }

  async function handleWiseConnected() {
    setShowWiseSetup(false);
    await refreshBusinesses();
  }

  async function handleMercuryConnected() {
    setShowMercurySetup(false);
    await refreshBusinesses();
  }

  const isConnected = activeProvider === 'wise' ? wiseConnected : mercuryConnected;
  const hasAnyConnection = wiseConnected || mercuryConnected;

  const totalWiseUsd = balances.reduce((sum, b) => b.currency === 'USD' ? sum + b.amount.value : sum, 0);
  const totalCurrencies = balances.length;
  const creditTxns = transactions.filter((t) => t.type === 'CREDIT');
  const debitTxns = transactions.filter((t) => t.type === 'DEBIT');
  const totalIn = creditTxns.reduce((s, t) => s + Math.abs(t.amount.value), 0);
  const totalOut = debitTxns.reduce((s, t) => s + Math.abs(t.amount.value), 0);

  const totalMercuryBalance = mercuryAccounts.reduce((sum, a) => sum + a.currentBalance, 0);
  const mercuryTotalIn = mercuryTransactions.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const mercuryTotalOut = mercuryTransactions.filter((t) => t.amount < 0).reduce((s, t) => s + Math.abs(t.amount), 0);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <LogoSpinner size={48} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="glass-header shrink-0 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-white tracking-tight">Wise/Mercury</h1>
            <p className="text-sm text-white hidden sm:block">
              {hasAnyConnection
                ? activeProvider === 'wise'
                  ? `${selectedProfile?.fullName || 'Wise'} - ${totalCurrencies} ${totalCurrencies === 1 ? 'currency' : 'currencies'}`
                  : `Mercury - ${mercuryAccounts.length} ${mercuryAccounts.length === 1 ? 'account' : 'accounts'}`
                : 'Connect your accounts'
              }
            </p>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {hasAnyConnection && (
              <ProviderToggle
                activeProvider={activeProvider}
                onSwitch={setActiveProvider}
                wiseConnected={wiseConnected}
                mercuryConnected={mercuryConnected}
              />
            )}
            {isConnected && (
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 glass-panel rounded-xl hover:shadow-md transition-all duration-200 disabled:opacity-50 text-xs font-medium text-white"
                title="Refresh"
              >
                <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            )}
            <button
              onClick={() => activeProvider === 'wise' ? setShowWiseSetup(true) : setShowMercurySetup(true)}
              className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 text-white text-xs font-semibold rounded-xl glass-button-active"
              title={isConnected ? 'Update Key' : `Connect ${activeProvider === 'wise' ? 'Wise' : 'Mercury'}`}
            >
              <Plus size={14} />
              <span className="hidden sm:inline">
                {isConnected ? 'Update Key' : `Connect ${activeProvider === 'wise' ? 'Wise' : 'Mercury'}`}
              </span>
            </button>
          </div>
        </div>
        {hasAnyConnection && (
          <p className="text-xs text-white mt-2 sm:hidden">
            {activeProvider === 'wise'
              ? `${selectedProfile?.fullName || 'Wise'} \u00b7 ${totalCurrencies} ${totalCurrencies === 1 ? 'currency' : 'currencies'}`
              : `Mercury \u00b7 ${mercuryAccounts.length} ${mercuryAccounts.length === 1 ? 'account' : 'accounts'}`
            }
          </p>
        )}
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        {error && (
          <div className="bg-red-500/10 border border-white/10 rounded-2xl p-4 mb-4 flex items-center gap-3">
            <AlertCircle size={18} className="text-red-500 shrink-0" />
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}

        {!isConnected ? (
          <EmptyState
            provider={activeProvider}
            onConnectWise={() => setShowWiseSetup(true)}
            onConnectMercury={() => setShowMercurySetup(true)}
          />
        ) : activeProvider === 'wise' ? (
          <>
            {balances.length > 0 && selectedBalance && (
              <div className="grid grid-cols-2 fold:grid-cols-4 gap-3 sm:gap-4 mb-4">
                <SummaryCard
                  label="USD Balance"
                  value={formatCurrencyValue(totalWiseUsd, 'USD')}
                  sub="Total USD holdings"
                  icon={Wallet}
                  iconBg="bg-emerald-500/10"
                  iconColor="text-emerald-800"
                />
                <SummaryCard
                  label="Currencies"
                  value={totalCurrencies.toString()}
                  sub="Active balances"
                  icon={ArrowLeftRight}
                  iconBg="bg-sky-500/10"
                  iconColor="text-sky-600"
                />
                <SummaryCard
                  label="Total In"
                  value={formatCurrencyValue(totalIn, selectedBalance.currency)}
                  sub="All-time credits"
                  icon={TrendingUp}
                  iconBg="bg-emerald-500/10"
                  iconColor="text-emerald-800"
                  valueColor="text-emerald-800"
                />
                <SummaryCard
                  label="Total Out"
                  value={formatCurrencyValue(totalOut, selectedBalance.currency)}
                  sub="All-time debits"
                  icon={TrendingDown}
                  iconBg="bg-red-500/10"
                  iconColor="text-red-600"
                  valueColor="text-red-600"
                />
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mb-4">
              {balances.map((balance) => (
                <BalanceCard
                  key={balance.id}
                  balance={balance}
                  isSelected={selectedBalance?.id === balance.id}
                  onClick={() => handleBalanceSelect(balance)}
                />
              ))}
            </div>

            {selectedBalance && (
              <StatementSection
                transactions={transactions}
                loading={statementLoading}
                currency={selectedBalance.currency}
              />
            )}
          </>
        ) : (
          <>
            {mercuryAccounts.length > 0 && (
              <div className="grid grid-cols-2 fold:grid-cols-4 gap-3 sm:gap-4 mb-4">
                <SummaryCard
                  label="Total Balance"
                  value={formatCurrencyValue(totalMercuryBalance, 'USD')}
                  sub="All accounts"
                  icon={Building2}
                  iconBg="bg-emerald-500/10"
                  iconColor="text-emerald-800"
                />
                <SummaryCard
                  label="Accounts"
                  value={mercuryAccounts.length.toString()}
                  sub="Active accounts"
                  icon={Wallet}
                  iconBg="bg-sky-500/10"
                  iconColor="text-sky-600"
                />
                <SummaryCard
                  label="Total In"
                  value={formatCurrencyValue(mercuryTotalIn, 'USD')}
                  sub="All-time credits"
                  icon={TrendingUp}
                  iconBg="bg-emerald-500/10"
                  iconColor="text-emerald-800"
                  valueColor="text-emerald-800"
                />
                <SummaryCard
                  label="Total Out"
                  value={formatCurrencyValue(mercuryTotalOut, 'USD')}
                  sub="All-time debits"
                  icon={TrendingDown}
                  iconBg="bg-red-500/10"
                  iconColor="text-red-600"
                  valueColor="text-red-600"
                />
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mb-4">
              {mercuryAccounts.map((account) => (
                <MercuryAccountCard
                  key={account.id}
                  account={account}
                  isSelected={selectedMercuryAccount?.id === account.id}
                  onClick={() => handleMercuryAccountSelect(account)}
                />
              ))}
            </div>

            {selectedMercuryAccount && (
              <MercuryTransactionsSection
                transactions={mercuryTransactions}
                loading={mercuryTxnLoading}
              />
            )}
          </>
        )}
      </div>

      {showWiseSetup && (
        <WiseSetupModal
          onClose={() => setShowWiseSetup(false)}
          onConnected={handleWiseConnected}
        />
      )}
      {showMercurySetup && (
        <MercurySetupModal
          onClose={() => setShowMercurySetup(false)}
          onConnected={handleMercuryConnected}
        />
      )}
    </div>
  );
}

function ProviderToggle({
  activeProvider,
  onSwitch,
  wiseConnected,
  mercuryConnected,
}: {
  activeProvider: Provider;
  onSwitch: (p: Provider) => void;
  wiseConnected: boolean;
  mercuryConnected: boolean;
}) {
  return (
    <div className="flex items-center bg-white/10 rounded-xl p-0.5">
      <button
        onClick={() => onSwitch('wise')}
        className={`flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-1.5 text-[11px] font-semibold rounded-lg transition-all duration-200 ${
          activeProvider === 'wise'
            ? 'bg-white text-teal-700 shadow-sm'
            : 'text-white hover:text-white'
        }`}
        title="Wise"
      >
        <Wallet size={14} className="sm:w-3 sm:h-3" />
        <span className="hidden sm:inline">Wise</span>
        {wiseConnected && (
          <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
        )}
      </button>
      <button
        onClick={() => onSwitch('mercury')}
        className={`flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-1.5 text-[11px] font-semibold rounded-lg transition-all duration-200 ${
          activeProvider === 'mercury'
            ? 'bg-white text-sky-700 shadow-sm'
            : 'text-white hover:text-white'
        }`}
        title="Mercury"
      >
        <Building2 size={14} className="sm:w-3 sm:h-3" />
        <span className="hidden sm:inline">Mercury</span>
        {mercuryConnected && (
          <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
        )}
      </button>
    </div>
  );
}

function SummaryCard({
  label, value, sub, icon: Icon, iconBg, iconColor, valueColor,
}: {
  label: string;
  value: string;
  sub: string;
  icon: typeof Wallet;
  iconBg: string;
  iconColor: string;
  valueColor?: string;
}) {
  return (
    <div className="glass-panel rounded-2xl p-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-default">
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg}`}>
          <Icon size={15} className={iconColor} />
        </div>
        <span className="text-xs font-medium text-white">{label}</span>
      </div>
      <p className={`text-lg font-bold tracking-tight tabular-nums ${valueColor || 'text-white'}`}>
        {value}
      </p>
      <p className="text-[11px] text-white mt-0.5">{sub}</p>
    </div>
  );
}

function EmptyState({
  provider,
  onConnectWise,
  onConnectMercury,
}: {
  provider: Provider;
  onConnectWise: () => void;
  onConnectMercury: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-6 shadow-sm ${
        provider === 'wise'
          ? 'bg-gradient-to-br from-teal-50 to-emerald-50'
          : 'bg-gradient-to-br from-sky-50 to-blue-50'
      }`}>
        {provider === 'wise'
          ? <Wallet size={36} className="text-teal-600" />
          : <Building2 size={36} className="text-sky-600" />
        }
      </div>
      <h2 className="text-lg font-bold text-white mb-2">Connect Your Accounts</h2>
      <p className="text-sm text-white text-center max-w-md mb-6 leading-relaxed">
        Link your bank accounts to see real-time balances and track transaction history.
      </p>
      <div className="flex items-center gap-3">
        <button
          onClick={onConnectWise}
          className="flex items-center gap-2 px-5 py-2.5 glass-button-active text-white text-sm font-semibold rounded-xl"
        >
          <Wallet size={16} />
          Connect Wise
        </button>
        <button
          onClick={onConnectMercury}
          className="flex items-center gap-2 px-5 py-2.5 glass-button-active text-white text-sm font-semibold rounded-xl"
        >
          <Building2 size={16} />
          Connect Mercury
        </button>
      </div>
    </div>
  );
}
